import AddIcon from "@mui/icons-material/Add";
import { FormControlLabel, Stack, Switch, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import { GridColumnOrderChangeParams } from "@mui/x-data-grid-pro";
import { ChangeEvent, ReactElement, useContext, useEffect, useMemo } from "react";

import IconAddFolder from "../../assets/images/icons/icon_create_folder.svg";
import IconDeleteRed from "../../assets/images/icons/icon_delete_red.svg";
import IconDown from "../../assets/images/icons/icon_down.svg";
import IconPencil from "../../assets/images/icons/icon_pencil.svg";
import IconUp from "../../assets/images/icons/icon_up.svg";
import IconUpload from "../../assets/images/icons/icon_upload_files.svg";
import IconWarning from "../../assets/images/icons/icon_warning.svg";
import { DataGrid, PageLock, ProgressModal } from "../../components";
import ArrowPopover from "../../components/ArrowPopover/ArrowPopover";
import BootstrapInput from "../../components/BootstrapInput/BootstrapInput";
import Button from "../../components/Buttons/Button/Button";
import SplitButton from "../../components/Buttons/SplitButton/SplitButton";
import Filter from "../../components/DataGrid/Filter/Filter";
import { FolderTree } from "../../components/FolderTree/FolderTree";
import ImgIcon from "../../components/ImgIcon/ImgIcon";
import ListItem from "../../components/ListItem/ListItem";
import ListItemIcon from "../../components/ListItemIcon/ListItemIcon";
import ConfirmationDialog from "../../components/Modal/ConfirmationDialog";
import DeleteConfirmationDialog from "../../components/Modal/DeleteConfirmationDialog/DeleteConfirmationDialog";
import NextStepDialog from "../../components/Modal/NextStepDialog/NextStepDialog";
import { AppContext } from "../../core/context/appContextProvider";
import useRole from "../../core/routing/useRole";
import { saveColumnOrder } from "../../services/columnOrder.service";
import { DOCUMENT_SUB_FOLDERS_ORDER_VIEW_KEY } from "../../utils/constants/text.constants";
import { arrayIndexUpdate as updateArrayIndex } from "../../utils/helpers/columnOrder.helper";
import { DocumentFilter } from "../../utils/types/documents.type";
import { DataGridColDef } from "../../utils/types/listItems";
import { ScopeRole } from "../../utils/types/user.type";
import { DocumentsBreadcrumbs } from "./components/breadcrumbs";
import EditPanel from "./components/editPanel/EditPanel";
import { Errors } from "./components/errors/Errors";
import PermissionChips from "./components/permissionsChips/PermissionChips";
import UploadFiles from "./components/uploadFiles/UploadFiles";
import { DocumentsContext } from "./context";
import {
  ButtonBox,
  ChipsContent,
  DocumentBox,
  HeaderRow,
  PermissionBox,
} from "./Documents.styles";
import {
  useDocumentDataList,
  useDocuments,
  useDocumentsBulkActions,
  useDocumentsGridHeaders,
} from "./hooks";
import { useDocumentsHelper } from "./hooks/useDocumentsHelper.hooks";


const ContextMenuOptions = [
  {
    label: "Rename",
    id: "edit_folder",
    icon: IconPencil,
    type: "edit",
    forceVisible: true,
  },
  {
    label: "Move Up",
    id: "move_up",
    icon: IconUp,
    type: "move_up",
  },
  {
    label: "Move Down",
    id: "move_down",
    icon: IconDown,
    type: "move_down",
    divider: true,
  },
  {
    label: "Delete",
    id: "delete_folder",
    icon: IconDeleteRed,
    type: "delete",
    forceVisible: true,
  },
];

const AddNewOptions = [
  {
    label: "Upload Files",
    id: "upload_files",
    icon: IconUpload,
    type: "upload",
  },
  {
    label: "Create Folder",
    id: "create_folder",
    icon: IconAddFolder,
    type: "create",
  },
];

function Documents(): ReactElement {
  const theme = useTheme();
  const { state: appState, informationAlert } = useContext(AppContext);
  const { state, ...actions } = useDocuments();

  const { hasRole: isClientAdmin } = useRole([ScopeRole.ARK_CLIENT_ADMIN]);

  const { hasRole: isSuperAdmin } = useRole([ScopeRole.SUPER_ADMIN]);

  const { hasRole: isFundAdmin } = useRole([ScopeRole.FUND_USER_ADMIN]);

  const { hasRole: isBasicAdmin } = useRole([ScopeRole.BASIC_ADMIN]);

  const { hasRole: isBasicUser } = useRole([ScopeRole.BASIC_USER]);

  const isEditable: boolean = !!isSuperAdmin || !!isClientAdmin || !!isBasicAdmin;
  const isAdmin: boolean = !!(isSuperAdmin || isFundAdmin || isClientAdmin || isBasicAdmin);
  const clientId = state.documents?.clientId || appState.loginUser.clientId;
  const clientName = appState.loginUser.clientName || "Default Client";

  useEffect(() => {
    if (clientId) {
      if (isEditable) {
        actions.initialize(clientId);
      } else {
        actions
          .fetchDocuments({ isAdmin })
          .then((documents) => {
            if (documents.folders?.some(item => item.requiresConsent)) {
              if (documents.clientId) {
                actions.fetchConsent({
                  clientId: documents.clientId,
                  consentType: 'K1'
                });
              }
            }
          })
          .catch(err => { });
      }
    }
  }, [clientId, isEditable]);

  const {
    contextMenu,
    selectedPermissions,
    showDeleteConfirmationBox,
    lockedTooltipText,
    showAddFolderDialog,
    currentStep,
    selectedFiles,
    folderName,
    isRootView,
    addNewAnchorEl,
    showAddOptions,
    showUploadFiles,
    newFiles,
    editing,
    showEditPanel,
    showMovePanel,
    selectedFolder,
    contextMenuMoveOptions,
    error,
    showConsent,
    viewConfig,
    resetOnClose,
    onDeleteCancel,
    onDeleteConfirm,
    handleFolderNameChange,
    handleNextAction,
    handleSelectPermission,
    handleAllPermissionSelection,
    handleOnActionItemClick,
    handleAddNewFolderAction,
    handleOnCellClick,
    removeSelectedPermission,
    handleSelectionModelChange,
    handlePermissionUpdate,
    handleContextMenu,
    handleOnContextMenuClose,
    handleContextMenuOption,
    handleAddNewAction,
    handleOnAddNewActionClose,
    handleAddNewPopover,
    handleCloseUploadFiles,
    addNewFiles,
    handleFilter,
    handleSort,
    closeEditPanel,
    resetSelectedFiles,
    handleMoveFiles,
    handleFolderSelection,
    openDocument,
    clearError,
    acceptConsent,
    declineConsent,
    onSubFolderColumnOrderChange,
    reloadCurrentFolder,
    onNextPage,
    confirmFileRename,
    handleFileNameChange,
    showRenameFileDialog,
    fileName,
    renameLoading,
    setShowPrompt,
    showPrompt,
    handleWarningPrompt,
    setNameColumnWidth,
    nameColumnWidth,
    showDeleteOverflowPrompt,
    setShowDeleteOverflowPrompt,
    handleDeleteOverflowPrompt,
  } = useDocumentsHelper({ state, actions, isEditable, isAdmin, informationAlert, clientName });

  const { isLoading, dataList } = useDocumentDataList(state, {
    newFiles,
    selectedFiles,
    isEditable,
    nameColumnWidth,
  });

  const headerList: Array<DataGridColDef> = useDocumentsGridHeaders(state, {
    handleOnChipClick: handlePermissionUpdate,
    isEditable,
    isAdmin,
    viewConfig,
    nameColumnWidth
  });

  const onRowColumnOrderChange = (
    params: GridColumnOrderChangeParams
  ) => {
    if (!isEditable || state.isPageLocked) return;

    const newIndex = params.targetIndex;
    const oldIndex = params.oldIndex;

    const columnOrderToSave = updateArrayIndex(headerList, oldIndex - 1, newIndex - 1, clientId, DOCUMENT_SUB_FOLDERS_ORDER_VIEW_KEY);


    if (isAdmin) {
      saveColumnOrder(columnOrderToSave)
        .then(res => {
          onSubFolderColumnOrderChange();
        });
    }
  };

  const folderCount = selectedFiles.filter(
    (item) => item.type === "folder"
  ).length;

  const fileCount = selectedFiles.length - folderCount;
  const allowDelete =
    folderCount === 0 ? fileCount > 0 : folderCount === 1 && fileCount >= 0;

  const availableBulkActions = useDocumentsBulkActions(state, {
    allowDelete,
    folderCount,
    fileCount,
    isEditable,
    haveFolders:
      selectedFiles.filter((item) => item.type === "folder").length > 0,
    isUser: isBasicUser,
  });

  const renderEditPanel = useMemo(
    () => (
      <EditPanel
        title="Bulk Edit"
        open={showEditPanel}
        files={selectedFiles}
        handleClose={closeEditPanel}
      />
    ),
    [showEditPanel, selectedFiles]
  );

  const showFolders: boolean = state.selectedFilters.showFolders;

  return (
    <DocumentsContext.Provider
      value={{
        state,
        ...actions,
        resetOnChange: resetOnClose,
        resetSelectedFiles,
        reloadCurrentFolder,
        openDocument,
      }}
    >
      <DocumentBox id="documents_list_main">
        <HeaderRow>
          <DocumentsBreadcrumbs />
          {isEditable ? (
            <ButtonBox>
              {!isRootView && <FormControlLabel
                control={
                  <Switch
                    checked={showFolders}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                      handleFilter(DocumentFilter.Folder, e.target.checked);
                    }}
                    name="folders visibility"
                  />
                }
                label={showFolders ? "Show Folders" : "Hide Folders"}
              />}
              {state.selectedRows.length > 0 && <SplitButton
                id={"btn_bulk_action_options"}
                text="Actions"
                options={availableBulkActions}
                hidden={false}
                handleOptionClick={handleOnActionItemClick}
                ariaLabelMessage="Select bulk action option"
                showSelectedOptionIcon={false}
                highlightSelections={false}
                stopClickForwarding={true}
              />}
              <Button
                id="btn_add_new_folder"
                color="primary"
                onClick={
                  isRootView ? handleAddNewFolderAction : handleAddNewAction
                }
                icon={<AddIcon />}
                text={isRootView ? "New Folder" : "Add New"}
              />
            </ButtonBox>
          ) : (
            <ButtonBox>
              {!isRootView && <FormControlLabel
                control={
                  <Switch
                    checked={showFolders}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                      handleFilter(DocumentFilter.Folder, e.target.checked);
                    }}
                    name="folders visibility"
                  />
                }
                label={showFolders ? "Show Folders" : "Hide Folders"}
              />}
              {state.selectedRows.length > 0 && <SplitButton
                id={"btn_bulk_action_options"}
                text="Download"
                options={availableBulkActions}
                hidden={false}
                handleOptionClick={handleOnActionItemClick}
                ariaLabelMessage="Select bulk action option"
                showSelectedOptionIcon={false}
                highlightSelections={false}
              />}
            </ButtonBox>
          )}
        </HeaderRow>
        {state.documents && (
          <DataGrid
            id="documents_data_grid"
            checkboxSelection={true}
            dataList={dataList}
            headerList={headerList}
            noDataMessage={isLoading ? "Fetching Data..." : undefined}
            minHeight="1vh"
            autoHeight={false}
            sortingMode={state?.cursor?.length > 0 ? "server" : "client"}
            onCellClick={handleOnCellClick}
            onColumnWidthChange={
              (params) => {
                if (params.colDef.field === "name") {
                  setNameColumnWidth(params.width);
                }
              }
            }
            handleFilter={handleFilter}
            componentsProps={{
              cell: {
                onContextMenu: isEditable && !state.isPageLocked ? handleContextMenu : undefined,
              },
            }}
            onSelectionModelChange={(selectionModel) => {
              handleSelectionModelChange(selectionModel, dataList);
            }}
            selectionModel={selectedFiles.map(item => item.id)}
            onSortModelChange={handleSort}
            onColumnOrderChange={onRowColumnOrderChange}
            onNextPage={onNextPage}
          />
        )}
        {isEditable && (
          <PageLock
            locked={state.isPageLocked}
            disabled={false}
            showTooltip={Boolean(lockedTooltipText)}
            tooltipText={lockedTooltipText}
            onChange={actions.togglePageLock}
          />
        )}
        <NextStepDialog
          open={showAddFolderDialog && currentStep === "folder-name"}
          onClose={resetOnClose}
          fullWidth={true}
          id="create_new_folder"
          actionDisplayType="inline"
          actions={[
            {
              label: "Cancel",
              onClick: resetOnClose,
              id: "cancel_button",
              variant: "outlined",
              color: "error",
            },
            {
              label: isRootView ? "Next" : "OK",
              onClick: () =>
                handleNextAction(
                  isRootView
                    ? "assign-permissions"
                    : editing
                      ? "rename-folder"
                      : "create-folder"
                ),
              id: "next_button",
              variant: "contained",
              color: "primary",
            },
          ]}
          title={editing ? "Rename" : "New Folder"}
        >
          <Box>
            <BootstrapInput
              id="new_folder_name"
              label="Folder Name"
              autoFocus={true}
              fullWidth={true}
              value={folderName}
              onChange={handleFolderNameChange}
            />
          </Box>
        </NextStepDialog>
        <NextStepDialog
          open={
            showAddFolderDialog &&
            isRootView &&
            (currentStep === "assign-permissions" ||
              currentStep === "create-folder" ||
              currentStep === "edit-folder")
          }
          onClose={resetOnClose}
          fullWidth={true}
          id="new_folder_permissions"
          actionDisplayType="inline"
          actions={[
            {
              label: "Cancel",
              onClick: resetOnClose,
              id: "cancel_button",
              variant: "outlined",
              color: "error",
            },
            {
              label: "OK",
              onClick: () =>
                handleNextAction(editing ? "edit-folder" : "create-folder"),
              id: "ok_button",
              variant: "contained",
              color: "primary",
            },
          ]}
          title="Permissions"
        >
          <Box flex={1}>
            <Grid container rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <Filter
                  handleSelection={handleSelectPermission}
                  handleAllSelection={handleAllPermissionSelection}
                  options={state.permissions}
                  selectedOptions={selectedPermissions.map((item) => item.id)}
                  idField="key"
                  labelField="name"
                  popoverStyle={{
                    border: `1px solid ${theme.palette.secondary.dark}`,
                    padding: theme.spacing(1),
                    borderRadius: 4,
                  }}
                  scrollContainerStyle={{ maxWidth: "100%", maxHeight: 400 }}
                  enableApplyAction={false}
                  applyOnSelect={true}
                  clearable={false}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PermissionBox>
                  <ChipsContent>
                    <PermissionChips
                      removeSelected={removeSelectedPermission}
                      permissions={selectedPermissions}
                    />
                  </ChipsContent>
                </PermissionBox>
              </Grid>
            </Grid>
          </Box>
        </NextStepDialog>
        <NextStepDialog
          open={showRenameFileDialog && currentStep === "file-rename"}
          onClose={resetOnClose}
          fullWidth={true}
          id="rename_file"
          actionDisplayType="inline"
          actions={[
            {
              label: "Cancel",
              onClick: resetOnClose,
              id: "cancel_file_rename_button",
              variant: "outlined",
              color: "error",
            },
            {
              label: "OK",
              onClick: () => confirmFileRename(),
              id: "done_file_rename_button",
              variant: "contained",
              color: "primary",
            },
          ]}
          title={"Rename"}
        >
          <Box>
            <BootstrapInput
              id="file_rename_input"
              label="File Name"
              autoFocus={true}
              fullWidth={true}
              value={fileName}
              onChange={handleFileNameChange}
            />
          </Box>
        </NextStepDialog>
        <ArrowPopover
          id="context_menu_popover"
          anchorEl={contextMenu?.lastChild || null}
          showPopover={Boolean(contextMenu)}
          handleOnPopoverClose={handleOnContextMenuClose}
          content={
            <List dense={true}>
              {ContextMenuOptions?.filter(option => contextMenuMoveOptions === "only_rename" ? option.type === "edit" : true).map((option) => (
                <ListItem
                  key={option.id}
                  sx={{
                    display:
                      contextMenuMoveOptions === option.type ||
                        contextMenuMoveOptions === "both" ||
                        option.forceVisible
                        ? "initial"
                        : "none",
                  }}
                >
                  <ListItemButton
                    onClick={(event) =>
                      handleContextMenuOption(event, option.type)
                    }
                    divider={option.divider}
                  >
                    <ListItemIcon id={`${option.id}`} icon={option.icon} />
                    <Typography variant="subtitle2">
                      {option.id === "edit_folder" && isRootView
                        ? "Edit"
                        : option.label}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          }
        />
        <ArrowPopover
          id="add_menu_popover"
          anchorEl={addNewAnchorEl}
          showPopover={showAddOptions}
          handleOnPopoverClose={handleOnAddNewActionClose}
          content={
            <List dense={true}>
              {AddNewOptions?.map((option) => (
                <ListItem key={option.id}>
                  <ListItemButton
                    onClick={(event) => handleAddNewPopover(event, option.type)}
                  >
                    <ListItemIcon id={`${option.id}`} icon={option.icon} />
                    <Typography variant="subtitle2">{option.label}</Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          }
        />
        <ProgressModal id="documents_progress_modal" showProgress={isLoading || renameLoading} />
        {showConsent && <ConfirmationDialog
          open={showConsent}
          onClose={() => { }}
          id="consent_confirmation"
          actionDisplayType="inline"
          actions={[
            {
              label: 'Accept',
              onClick: acceptConsent,
              id: 'accept_button',
              variant: 'contained',
              color: 'primary'
            },
            {
              label: 'Decline',
              onClick: declineConsent,
              id: 'decline_button',
              variant: 'outlined',
              color: 'error'
            }
          ]}
          title="Consent confirmation"
          content={state.consent}
        />}
        {showPrompt && <ConfirmationDialog
          open={showPrompt}
          onClose={() => setShowPrompt(false)}
          id="prompt_box_publish"
          actionDisplayType="inline"
          actions={[
            {
              label: 'OK',
              onClick: handleWarningPrompt,
              id: 'ok_button',
              variant: 'contained',
              color: 'primary'
            },
          ]}
          title="Warning"
          content="The files without investors and funds will not be available on client portal."
        />
        }
        {showDeleteOverflowPrompt && <ConfirmationDialog
          open={showDeleteOverflowPrompt}
          onClose={() => setShowDeleteOverflowPrompt(false)}
          id="overflow_prompt_box_publish"
          actionDisplayType="inline"
          actions={[
            {
              label: 'OK',
              onClick: handleDeleteOverflowPrompt,
              id: 'ok_button',
              variant: 'contained',
              color: 'primary'
            },
          ]}
          title="Alert"
          content="Please only select 150 or less files to delete"
        />
        }
        <DeleteConfirmationDialog
          open={showDeleteConfirmationBox}
          id="delete_confirmation_modal"
          primaryButtonAction={onDeleteConfirm}
          secondaryButtonAction={onDeleteCancel}
        />
        <UploadFiles
          showDialog={showUploadFiles}
          closeDialog={handleCloseUploadFiles}
          reload={actions.fetchFolderContent}
          addNewFiles={addNewFiles}
        />
      </DocumentBox>
      {renderEditPanel}
      <NextStepDialog
        open={showMovePanel}
        onClose={resetOnClose}
        fullWidth={true}
        id="move_files_in_folder"
        actionDisplayType="inline"
        actions={[
          {
            label: "Cancel",
            onClick: resetOnClose,
            id: "cancel_button",
            variant: "outlined",
            color: "error",
          },
          {
            label: "OK",
            onClick: handleMoveFiles,
            id: "ok_button",
            variant: "contained",
            color: "primary",
          },
        ]}
        title="Move File To"
      >
        <Box flex={1}>
          <Typography variant="filterUnchecked">
            Select the folder you want to move files to.
          </Typography>
          <FolderTree
            structure={state?.tree?.folders ?? []}
            onSelect={handleFolderSelection}
            selectedFolder={selectedFolder}
          />
        </Box>
      </NextStepDialog>
      {error && (
        <Errors
          open={!!error.title}
          title={
            <Stack
              gap={1}
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
            >
              <ImgIcon icon={IconWarning} />
              <Typography variant="h3" color="red">
                {error.title}
              </Typography>
            </Stack>
          }
          subTitle={error.subTitle}
          handleClose={clearError}
        />
      )}
    </DocumentsContext.Provider>
  );
}

export default Documents;
